import React from 'react'

/**
 * Toast component
 *
 * @component
 * @param {string|object} props.message - message for toast (string or object with message and type properties)
 * @param {Function} props.handleClose - function to close the toast
 * @returns {JSX.Element} The toast component.
 */
const Toast = ({ message, handleClose }) => {
  // Handle both string and object message formats
  const getMessageText = () => {
    if (typeof message === 'string') {
      return message
    }
    if (message && typeof message === 'object' && message.message) {
      return message.message
    }
    // Fallback: try to convert to string
    return String(message || '')
  }

  // Get toast type
  const getType = () => {
    if (message && typeof message === 'object' && message.type) {
      return message.type
    }
    return 'error' // Default type for backward compatibility
  }

  const type = getType()

  // Accessibility + stable hook for UI test automation (ADR 0008): error/warning
  // toasts are assertive alerts, success/info are polite status updates.
  const isAlert = type === 'error' || type === 'warning'

  // Circle color by severity (flat)
  const getCircleColor = () => {
    switch (type) {
      case 'success':
      case 'completion':
      case 'info':
        return '#22c55e' // green
      case 'warning':
        return '#eab308' // yellow
      case 'error':
      default:
        return '#ef4444' // red
    }
  }

  // Single icon: circle (severity color) + "!" or "✓" inside
  const isSuccess = type === 'success' || type === 'completion'
  const symbol = isSuccess ? '✓' : '!'

  // Get background color based on type
  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
      case 'completion':
        return 'bg-green-500/60 border-green-400'
      case 'warning':
        return 'bg-yellow-500/60 border-yellow-400'
      case 'info':
        return 'bg-blue-500/60 border-blue-400'
      case 'error':
      default:
        return 'bg-pale-blue/60 border-blue'
    }
  }

  return (
    <li
      data-testid={`toast-${type}`}
      role={isAlert ? 'alert' : 'status'}
      aria-live={isAlert ? 'assertive' : 'polite'}
      className={`relative w-[348px] text-white backdrop-blur-lg border-2 rounded-lg animate-slideUp ${getBackgroundColor()}`}
    >
      <button
        className="w-full flex gap-2 p-2 text-left overflow-x-hidden overflow-y-auto items-start"
        onClick={handleClose}
      >
        {/* Icon: circle colored by severity + ! or ✓ inside */}
        <span
          className="flex-shrink-0 w-6 h-6 min-w-[24px] min-h-[24px] rounded-full flex items-center justify-center text-white font-bold text-sm leading-none"
          style={{ backgroundColor: getCircleColor() }}
          aria-hidden
        >
          {symbol}
        </span>
        <p className="flex-1 min-w-0 font-light text-sm desktop:text-base leading-snug pt-0.5">
          {getMessageText()}
        </p>
      </button>
    </li>
  )
}

export default Toast
